import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { grantableRoles } from '../helpers/roles';
import { unauthenticatableRoles } from '../constants/roles';
import useGlobalSelectionsContext from './useGlobalSelectionsContext';

/**
 * Returns role options filtered by what the current user can grant, based on their initiative
 * and organization roles, admin mode, and super-admin status.
 *
 * @param {boolean} [onlyAuthenticatableRoles=false] - when true, excludes roles in
 *   unauthenticatableRoles (e.g. initiative-assignee) from filteredRolesOptions
 * @returns {{ filteredRolesOptions: Array<{id: string, name: string}>, rolesOptions: Array<{id: string, name: string}> }}
 *   - filteredRolesOptions: roles the current user can grant (optionally filtered by authenticatability)
 *   - rolesOptions: unfiltered list of all platform roles from Redux
 */
const useUserRoles = (onlyAuthenticatableRoles = false) => {
  const { adminMode, isSuperAdmin } = useGlobalSelectionsContext();
  const { user } = useSelector((state) => state.auth.auth);
  const { list: rolesList } = useSelector(({ roles }) => roles.select);

  const [filteredRolesOptions, setFilteredRolesOptions] = useState([]);

  useEffect(() => {
    if (!user) {
      setFilteredRolesOptions([]);
      return;
    }

    setFilteredRolesOptions(
      grantableRoles({
        initiativeRole: user?.initiative_role,
        organizationRole: user?.organization_role,
        roles: rolesList,
        isInitiativeMode: !adminMode,
        isSuperAdmin,
      }).filter((role) => !onlyAuthenticatableRoles || !unauthenticatableRoles.includes(role.id)),
    );
  }, [adminMode, isSuperAdmin, rolesList, user, onlyAuthenticatableRoles]);

  return {
    filteredRolesOptions,
    rolesOptions: rolesList,
  };
};

export default useUserRoles;
